import React, { useMemo, useEffect, useRef } from "react";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "@src/../entrypoints/theme";
import {Paragraph} from "@mindarc/arctheme-components/frontend/src/stories/components/elements/Paragraph/Paragraph";
import {Headings} from "@mindarc/arctheme-components/frontend/src/stories/components/elements/Headings/Headings";
import Tile from "@src/stories/components/Tile/Tile";

export default function Carousel({
    settings,
    columns,
    headerText,
    subtext,
    tagline,
    columns_count,
    onColumnClick,
    sectionId,
}) {
    const { section } = settings;
    const carouselRef = useRef(null);
    const isMobile = useMobileBreakpoint(mobileBreakpoint);
    columns_count = isMobile ? section.columns_count_mobile : section.columns_count;
    const displayColumns = useMemo(() => {
        if (columns.length === 0 || columns_count <= 0 || columns.length > columns_count) {
            return columns;
        }

        const multiplier = Math.floor(columns_count / columns.length) + 1;
        return Array.from({ length: multiplier }, () => columns).flat();
    }, [columns, columns_count]);

    const isFirstColumnDefaultLayout = columns[0]?.settings.layout === "default";
    let firstBlockHeight = isMobile
        ? columns[0]?.settings.mobile_block_height
        : columns[0]?.settings.block_height;

    if (!isFirstColumnDefaultLayout) {
        firstBlockHeight = 0;
    }
    const gapBetweenBlocksX = isMobile
        ? settings.section.gap_mobile
        : settings.section.gap;
    const gapBetweenBlocksY = isMobile
        ? settings.section.gap_y_mobile
        : settings.section.gap_y;

    const titleStyle = isMobile
        ? settings.section.header_style_mobile
        : settings.section.header_style;

    const taglineStyle = isMobile
        ? settings.section.tagline_style_mobile
        : settings.section.tagline_style;

    const contentStyle = isMobile
        ? settings.section.subtext_style_mobile
        : settings.section.subtext_style;

    const justifyClassMap = {
        start: "justify-start",
        center: "justify-center",
        end: "justify-end",
    };

    const mobileJustifyClass =
        justifyClassMap[settings.section.content_alignment_mobile] || "justify-center";
    const desktopJustifyClass =
        `lg:${justifyClassMap[settings.section.content_alignment_desktop] || "justify-center"}`;

    const carouselSectionId = sectionId || "multicolumn-carousel";

    useEffect(() => {
        const carouselEl = carouselRef.current;

        if (!carouselEl) {
            return;
        }

        const triggerResize = () => window.dispatchEvent(new Event('resize'));

        const images = carouselEl.querySelectorAll("img");
        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener("load", triggerResize, { once: true });
            }
        });

        const timer = window.setTimeout(triggerResize, 120);

        return () => {
            window.clearTimeout(timer);
            images.forEach((img) => img.removeEventListener("load", triggerResize));
        };
    }, [isMobile, displayColumns.length]);

    return (
        <div className="multicolumn">
            {(tagline || headerText || subtext) && (
                <div className="multicolumn-content-top w-full lg:w-[700px] my-[16px]">
                    {tagline && <Paragraph text={tagline} type={taglineStyle}/>}
                    {headerText && <Headings text={headerText} type={titleStyle}/>}
                    {subtext && <Paragraph text={subtext} type={contentStyle}/>}
                </div>
            )}

            <div
                ref={carouselRef}
                key={`multicolumn-carousel-${isMobile ? "mobile" : "desktop"}`}
                className={`multicolumn-carousel carousel flex w-full
                ${settings.direction}
                ${mobileJustifyClass}
                ${desktopJustifyClass}`}
                style={{
                    minHeight: !isMobile && firstBlockHeight > 0 ? `${firstBlockHeight}px` : undefined,
                }}
                {...({
                    "x-data": "carousel",
                    "x-init": `$nextTick(() => { initialise(${settings.section.show_navigation_dots}, true, false, false, 0, '${carouselSectionId}', true, false) })`,
                    "x-ref": "carousel",
                })}
            >
                {displayColumns.map((column, index) => {
                    const columnWidth = isMobile
                        ? column.settings.width_mobile
                        : column.settings.width;
                    const defaultFlexBasis = `${100 / columns_count}%`;
                    const flexBasis =
                        columnWidth > 0
                            ? `calc(${columnWidth}% - ${gapBetweenBlocksX}px)`
                            : `calc(${defaultFlexBasis} - ${gapBetweenBlocksX}px)`;
                    const columnHeight = isMobile
                        ? column.settings.mobile_block_height
                        : column.settings.block_height;
                    const heightStyle = columnHeight > 0 ? `${columnHeight}px` : "auto";

                    return (
                        <div
                            key={column.key || index}
                            data-column-index={index}
                            style={{
                                width: flexBasis,
                                marginTop: `${gapBetweenBlocksY / 2}px`,
                                marginBottom: `${gapBetweenBlocksY / 2}px`,
                                marginLeft: `${gapBetweenBlocksX / 2}px`,
                                marginRight: `${gapBetweenBlocksX / 2}px`,
                            }}
                            className="box-border overflow-hidden"
                        >
                            <Tile
                                data={column}
                                layout={column.settings.layout}
                                height={heightStyle}
                                settings={settings}
                                onColumnClick={() => onColumnClick?.(column, index)}
                                columnIndex={index}
                                sectionId={sectionId}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
